import { useState } from 'react'
import { Phone, Mail, MapPin, Clock, Send, Check } from 'lucide-react'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { useRecaptcha } from '../hooks/useRecaptcha'
import { API_CONFIG } from '../config/api'
import PageHeader from '../components/PageHeader'
import Seo from '../components/Seo'

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    pax: '',
    dates: '',
    origin: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const { franchiseData, loading } = useFranchiseData()
  const { token: recaptchaToken, widgetRef: recaptchaWidgetRef, reset: resetRecaptcha, enabled: recaptchaEnabled } = useRecaptcha()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')

    if (recaptchaEnabled && !recaptchaToken) {
      setSubmitError('Please complete the reCAPTCHA checkbox before submitting.')
      setIsSubmitting(false)
      return
    }

    try {
      // Construct the URL with query parameters
      const params = new URLSearchParams({
        'lead[first_name]': formData.firstName,
        'lead[last_name]': formData.lastName,
        'lead[email]': formData.email,
        'lead[telephone]': formData.phone,
        'lead[message]': formData.message,
        'lead[franchise_id]': API_CONFIG.FRANCHISE_ID || '402',
        'lead[pax]': formData.pax,
        'lead[dates]': formData.dates,
        'lead[origin]': formData.origin,
        ...(recaptchaToken ? { 'g-recaptcha-response': recaptchaToken } : {})
      })

      const response = await fetch(`${API_CONFIG.BASE_URL}/leads?${params.toString()}`, {
        method: 'POST',
        headers: {
          'x-api-key': API_CONFIG.API_KEY,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        setSubmitSuccess(true)
        resetRecaptcha()
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          pax: '',
          dates: '',
          origin: '',
          message: ''
        })
        setTimeout(() => {
          setSubmitSuccess(false)
        }, 5000)
      } else {
        const errorData = await response.text()
        console.error('API Error:', response.status, errorData)
        throw new Error(`API Error: ${response.status}`)
      }
    } catch (error) {
      setSubmitError('Failed to send your message. Please try again.')
      console.error('Contact form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading || !franchiseData) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-300 rounded w-48 mx-auto"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white">
      <Seo
        title="Contact Us - Memento Travel Co."
        description="Ready to start planning your perfect getaway? Contact our travel experts for personalized assistance and unforgettable travel experiences."
        canonicalUrl={typeof window !== 'undefined' ? window.location.href : 'https://www.mementotravel.co/contact'}
        pageType="contact"
      />
      <PageHeader
        title="Get in Touch"
        description="Ready to start planning your perfect getaway? We're here to help you create unforgettable travel experiences."
      />
      {/* Split Layout Container */}
      <div className="min-h-screen flex flex-col lg:flex-row">
        {/* Left Side - White Background with Contact Info */}
        <div className="lg:w-2/5 bg-white p-12 lg:p-16 flex flex-col justify-center">
          <div className="max-w-md mx-auto lg:mx-0 lg:ml-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Get in Touch</h3>
            
            <div className="space-y-8">
              {franchiseData?.telephone && (
                <div className="flex items-start gap-5">
                  <div 
                    className="flex h-12 w-12 items-center justify-center rounded-lg flex-shrink-0"
                    style={{ background: '#006D77' }}
                  >
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">Phone</h4>
                    <p className="text-gray-600 text-base">{franchiseData.telephone} / +27 69 368 8545</p>
                    {franchiseData.emergency_telephone && (
                      <p className="text-sm text-gray-500 mt-1">Emergency: {franchiseData.emergency_telephone}</p>
                    )}
                  </div>
                </div>
              )}

              {franchiseData?.email && (
                <div className="flex items-start gap-5">
                  <div 
                    className="flex h-12 w-12 items-center justify-center rounded-lg flex-shrink-0"
                    style={{ background: '#006D77' }}
                  >
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">Email</h4>
                    <p className="text-gray-600 text-base">{franchiseData.email} / werner@mementotravelco.co.za</p>
                  </div>
                </div>
              )}

              {franchiseData?.address && (
                <div className="flex items-start gap-5">
                  <div 
                    className="flex h-12 w-12 items-center justify-center rounded-lg flex-shrink-0"
                    style={{ background: '#006D77' }}
                  >
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">Address</h4>
                    <p className="text-gray-600 text-base">{franchiseData.address}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-5">
                <div 
                  className="flex h-12 w-12 items-center justify-center rounded-lg flex-shrink-0"
                  style={{ background: '#006D77' }}
                >
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Business Hours</h4>
                  <div className="text-gray-600 text-base">
                    <p>Monday - Friday: 9:00 AM - 4:00 PM</p>
                    <p>Saturday & Sunday: Available for travel emergencies.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-16">
              <h4 className="text-lg font-semibold text-gray-900 mb-6">Follow Us</h4>
              <div className="flex gap-6">
                <a 
                  href="https://www.facebook.com/mementotravelco" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 text-base font-medium transition-colors"
                >
                  Facebook
                </a>
                <a 
                  href="https://www.instagram.com/mementotravelco" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-pink-600 text-base font-medium transition-colors"
                >
                  Instagram
                </a>
                <a 
                  href="https://api.whatsapp.com/send?phone=27693688545" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-green-600 text-base font-medium transition-colors"
                >
                  WhatsApp
                </a>
              </div>
              
              {/* Tree Link - Aligned with Instagram */}
              <div className="mt-6 flex items-center gap-4">
                <a 
                  href="https://linktr.ee/mementotravelco" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform duration-200 hover:scale-105 relative group"
                  style={{
                    animation: 'heartbeat 1.5s ease-in-out infinite'
                  }}
                >
                  <img 
                    src="/Get_yOUR_WEBSITE_WITH__1_-removebg-preview.png" 
                    alt="Click for all social media platforms" 
                    className="w-48 h-48 object-contain"
                  />
                    {/* Hover tooltip */}
                    <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                      Click to access all social platforms
                    </div>
                </a>
                <p className="text-gray-600 text-sm font-medium">
                  👈 Click here for all our social media platforms
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Gradient Blue Background with Form */}
        <div 
          className="lg:w-3/5 p-12 lg:p-16 flex flex-col justify-center"
          style={{ background: '#006D77' }}
        >
          <div className="max-w-2xl lg:mr-8">
            <h3 className="text-3xl lg:text-4xl font-serif text-white mb-8">Send us a message</h3>
            
            {/* Contact Form */}
            
            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <h4 className="text-lg font-medium text-green-600 mb-2">
                  Message Sent Successfully!
                </h4>
                <p className="text-gray-600">
                  Thank you for contacting us. We'll get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor="firstName" className="block text-sm font-semibold text-white">
                      First Name <span className="text-blue-300">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      id="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:border-white transition-all duration-200 hover:border-gray-200 bg-white/90 backdrop-blur-sm"
                      style={{'--tw-ring-color': '#006D77'} as React.CSSProperties}
                      placeholder="Enter your first name"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="lastName" className="block text-sm font-semibold text-white">
                      Last Name <span className="text-blue-300">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      id="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:border-white transition-all duration-200 hover:border-gray-200 bg-white/90 backdrop-blur-sm"
                      style={{'--tw-ring-color': '#006D77'} as React.CSSProperties}
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-semibold text-white">
                    Email Address <span className="text-blue-300">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:border-white transition-all duration-200 hover:border-gray-200 bg-white/90 backdrop-blur-sm"
                    style={{'--tw-ring-color': '#006D77'} as React.CSSProperties}
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="phone" className="block text-sm font-semibold text-white">
                    Phone Number <span className="text-blue-300">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:border-white transition-all duration-200 hover:border-gray-200 bg-white/90 backdrop-blur-sm"
                    style={{'--tw-ring-color': '#006D77'} as React.CSSProperties}
                    placeholder="+27 12 345 6789"
                  />
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor="pax" className="block text-sm font-semibold text-white">
                      Number of People <span className="text-blue-300">*</span>
                    </label>
                    <input
                      type="text"
                      name="pax"
                      id="pax"
                      required
                      value={formData.pax}
                      onChange={handleChange}
                      placeholder="e.g., 2 adults, 1 child"
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:border-white transition-all duration-200 hover:border-gray-200 bg-white/90 backdrop-blur-sm"
                      style={{'--tw-ring-color': '#006D77'} as React.CSSProperties}
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="dates" className="block text-sm font-semibold text-white">
                      Travel Dates <span className="text-blue-300">*</span>
                    </label>
                    <input
                      type="text"
                      name="dates"
                      id="dates"
                      required
                      value={formData.dates}
                      onChange={handleChange}
                      placeholder="e.g., December 2024"
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:border-white transition-all duration-200 hover:border-gray-200 bg-white/90 backdrop-blur-sm"
                      style={{'--tw-ring-color': '#006D77'} as React.CSSProperties}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="origin" className="block text-sm font-semibold text-white">
                    Departure City <span className="text-blue-300">*</span>
                  </label>
                  <input
                    type="text"
                    name="origin"
                    id="origin"
                    required
                    value={formData.origin}
                    onChange={handleChange}
                    placeholder="e.g., Johannesburg, Cape Town"
                    className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:border-white transition-all duration-200 hover:border-gray-200 bg-white/90 backdrop-blur-sm"
                    style={{'--tw-ring-color': '#006D77'} as React.CSSProperties}
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="block text-sm font-semibold text-white">
                    Additional Message
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your travel preferences, special requirements, or any questions you have..."
                    className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:border-white transition-all duration-200 hover:border-gray-200 bg-white/90 backdrop-blur-sm resize-none"
                    style={{'--tw-ring-color': '#006D77'} as React.CSSProperties}
                  />
                </div>

                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-red-600 text-sm font-medium">{submitError}</p>
                  </div>
                )}

                {recaptchaEnabled && (
                  <div className="flex justify-center">
                    <div ref={recaptchaWidgetRef}></div>
                  </div>
                )}

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 px-6 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none inline-flex items-center justify-center text-lg"
                    style={{ 
                      background: '#006D77',
                      borderColor: '#006D77'
                    }}
                  >
                    <Send className="h-5 w-5 mr-3" />
                    {isSubmitting ? 'Sending Message...' : 'Send Message'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-24 sm:py-32 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h3>
          
          <div className="mx-auto max-w-4xl">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">How far in advance should I book?</h4>
                <p className="text-gray-600 text-sm">We recommend booking at least 2-3 months in advance for the best availability and prices, especially for peak season travel.</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Do you offer travel insurance?</h4>
                <p className="text-gray-600 text-sm">Yes, we can arrange comprehensive travel insurance to protect your trip investment and provide peace of mind. We cover both corporate and leisure travel/trips.</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Can you create custom packages?</h4>
                <p className="text-gray-600 text-sm">Absolutely! Our travel experts specialize in creating personalized itineraries tailored to your preferences and budget.</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">What payment methods do you accept?</h4>
                <p className="text-gray-600 text-sm">We accept various payment methods including bank transfers, credit cards, and installment plans for your convenience.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 